<script setup lang="ts">
import {useModyApi} from "~/middelware/ModyApi";
import {BlogArticle} from "~/utils/BlogArticle";
import {useAsyncData} from "#app";
import { useUserDataStorage } from "~/store/userdata";
import type { FileUploadSelectEvent } from "primevue/fileupload";

const api = useModyApi()
const route = useRoute()
const userDataStore = useUserDataStorage()
const editMode = ref(false)
const confirm = useConfirm()
const router = useRouter()
const blogCategories = ref<{id:number,name:string}[]>([])
let editorContent = {}

const { toHtml, toText } = useEditorJsHtml()
const safeHtml = ref("")

function plainDescription(value: string | undefined): string {
  if (!value) return ''

  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

function truncateSeoDescription(value: string): string {
  return value.length > 160 ? `${value.slice(0, 159).trim()}…` : value
}

definePageMeta({
        middleware: "legacy-blog-article-redirect"
    }
)

const blogArticleSlug = computed(() => route.params.slug as string)
const { data:blogArticle } = await useAsyncData<BlogArticle>(
  `article-${blogArticleSlug.value}`,
  async () => {
    if(blogArticleSlug.value == "new"){return new BlogArticle().toJSON()}
    const p = await api.getBlogArticleBySlug(blogArticleSlug.value)
    return p.toJSON()
  },
  { watch: [blogArticleSlug] }
)


watchEffect(async () => {
    editorContent = JSON.parse(JSON.stringify(blogArticle.value?.jsonContent))
    if(blogArticleSlug.value == "new") editMode.value = true
    safeHtml.value = blogArticle.value!.jsonContent ? toHtml(blogArticle.value!.jsonContent) : ""

    blogCategories.value = await getBlogCategories()
})

const seoDescription = computed(() => {
  const description = plainDescription(blogArticle.value?.Description)
  if (description) return truncateSeoDescription(description)

  return blogArticle.value?.jsonContent ? toText(blogArticle.value.jsonContent, { maxLength: 160 }) : ''
})


useSeoMeta({
  title: () => blogArticle.value?.Title,
  description: () => seoDescription.value,
  ogTitle: () => blogArticle.value?.Title,
  ogDescription: () => seoDescription.value,
  ogImage: () => blogArticle.value?.image.id && blogArticle.value?.image.id >= 0 ? blogArticle.value?.image.url : null,
  twitterCard: 'summary_large_image',
})

const confirmRemoval = () => {
  confirm.require({
    message: 'Soll der Artikel wirklich entfernt werden?',
    header: 'ACHTUNG',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Abbrechen',
    acceptLabel: 'Entfernen',
    accept: () => {
      remove()
    }
  });
};

async function getBlogCategories(){
  return await api.getBlogCategories()
}
async function publish(){
  if(!blogArticle.value)return
  blogArticle.value.publishedAt = new Date()
  await save()
}
async function unpublish(){
  if(!blogArticle.value)return
  blogArticle.value.publishedAt = null
  await save()
}
async function save(){
  editMode.value = false
  if(!blogArticle.value) {
    alert("Fehler: Kein Artikel geladen")
    return
  }
  blogArticle.value.jsonContent = editorContent
  const tempBlogArticle = new BlogArticle().fromJSON(blogArticle.value!)
  try {
    if(blogArticleSlug.value == "new"){
        const response = await api.create("blog-articles/",tempBlogArticle.toApiJSON())
        router.push("/blog/article/"+response.data.data.attributes.slug)
    }
    else{
        await api.update("blog-articles/"+blogArticle.value.id, tempBlogArticle.toApiJSON())
    }
  } catch (error) {
    console.log(error)
  }
  safeHtml.value = blogArticle.value!.jsonContent ? toHtml(blogArticle.value!.jsonContent) : ""
}
async function remove(){
    await api.remove("blog-articles/"+blogArticle.value?.id)
    router.push("/blog")
}

async function onSelect(event:FileUploadSelectEvent){
  const newFile = (event.files as File[])[0]
  let response = {id:-1, url:""}
  if(newFile){
    response = await api.uploadFile(newFile)
  }

  blogArticle.value!.image = response
  blogArticle.value = {...blogArticle.value, image: response} as any
}
</script>

<template>
  <ConfirmDialog></ConfirmDialog>

  <div>
    <BlogSideMenu></BlogSideMenu>

    <!-- Hero-Bild mit Titel-Overlay -->
    <div v-if="!editMode && blogArticle?.image.url" class="relative w-full max-h-[540px] overflow-hidden">
      <img :src="blogArticle.image.url" class="w-full h-[480px] md:h-[540px] object-cover block" alt="" />
      <div class="absolute inset-0 bg-gradient-to-t from-[rgba(0,20,40,0.85)] via-[rgba(0,20,40,0.3)] to-transparent flex flex-col justify-end px-6 py-7 md:px-14 md:py-10">
        <div class="flex items-center gap-3 mb-3">
          <NuxtLink to="/blog" class="text-white/75 text-sm no-underline hover:text-white transition-colors flex items-center gap-1">
            <i class="pi pi-arrow-left"></i> Blog
          </NuxtLink>
          <span v-if="blogArticle.category?.name" class="text-xs px-2.5 py-0.5 bg-[#E39737]/20 text-[#E39737] rounded font-semibold uppercase tracking-wide">
            {{ blogArticle.category.name }}
          </span>
        </div>
        <h1 class="text-3xl md:text-4xl font-bold text-white leading-snug max-w-3xl mb-2">{{ blogArticle?.Title }}</h1>
        <div v-if="blogArticle.publishedAt" class="text-white/60 text-sm">
          {{ blogArticle.publishedAt.toLocaleDateString("de", { year: 'numeric', month: 'long', day: 'numeric' }) }}
        </div>
      </div>
    </div>

    <!-- Kein Bild: kompakter Header -->
    <div v-else-if="!editMode" class="bg-zinc-900 px-6 pt-10 pb-8">
      <div class="max-w-3xl mx-auto">
        <NuxtLink to="/blog" class="text-stone-400 text-sm no-underline hover:text-stone-200 transition-colors flex items-center gap-1 mb-4 w-fit">
          <i class="pi pi-arrow-left"></i> Blog
        </NuxtLink>
        <span v-if="blogArticle?.category?.name" class="block text-xs text-[#E39737] font-semibold uppercase tracking-wide mb-2">{{ blogArticle.category.name }}</span>
        <h1 class="text-3xl md:text-4xl font-bold text-white leading-snug mb-3">{{ blogArticle?.Title }}</h1>
        <div v-if="blogArticle?.publishedAt" class="text-stone-500 text-sm">
          {{ blogArticle.publishedAt.toLocaleDateString("de", { year: 'numeric', month: 'long', day: 'numeric' }) }}
        </div>
      </div>
    </div>

    <!-- Edit-Modus: Bild-Upload -->
    <div v-if="editMode" class="max-w-3xl mx-auto px-6 mt-5">
      <img v-if="blogArticle?.image.url" :src="blogArticle.image.url" class="max-h-[200px] object-contain block mb-3 rounded-xl" />
      <FileUpload
        class="mt-2"
        customUpload
        mode="basic"
        :choose-label="blogArticle?.image.url ? 'Bild ändern' : 'Bild auswählen'"
        @select="onSelect"
      ></FileUpload>
    </div>

    <!-- Edit-Modus: Titel & Metadaten -->
    <div v-if="editMode" class="max-w-3xl mx-auto px-6 pt-4">
      <input-text
        class="w-full"
        placeholder="Titel"
        v-model="blogArticle!.Title"
        size="small"
      ></input-text>
      <Select class="w-full mt-2" v-model="blogArticle!.category" option-label="name" :options="blogCategories"></Select>
      <Textarea class="w-full mt-2" rows="4" v-model="blogArticle!.Description" placeholder="Kurzbeschreibung"></Textarea>
    </div>

    <!-- Artikel-Inhalt -->
    <div class="bg-zinc-800 py-10">
      <div class="max-w-3xl mx-auto px-6">
        <LazyBlocksEditor v-if="blogArticle && editMode" v-model="editorContent" class="articleContent"></LazyBlocksEditor>
        <ClientOnly>
          <div v-if="!editMode && Object.keys(blogArticle!.jsonContent).length > 0" v-html="safeHtml" class="articleContent" />
        </ClientOnly>
      </div>
    </div>
  </div>


    <!-- CTA: Angebot anfordern -->
    <div v-if="!editMode" class="bg-zinc-900 py-16">
      <div class="max-w-3xl mx-auto px-6 text-center">
        <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">Angebot für dein Event anfordern</h2>
        <p class="text-stone-400 mb-8 max-w-xl mx-auto">
          Du planst eine Firmenfeier, ein Konzert oder ein Event? Wir erstellen dir ein maßgeschneidertes Angebot für Licht, Ton und Bühne.
        </p>
        <NuxtLink to="/shop/request" class="inline-block">
          <Button label="Jetzt Angebot anfordern" icon="pi pi-arrow-right" iconPos="right" class="px-8 py-3" />
        </NuxtLink>
      </div>
    </div>
  <!-- Admin-Aktionen -->
  <div v-if="userDataStore.loggedIn" class="flex justify-end flex-wrap gap-2 max-w-3xl mx-auto px-6 py-5">
    <Button v-if="!editMode" icon="pi pi-pencil" label="Bearbeiten" size="small" @click="editMode = true" />
    <Button v-if="editMode && !blogArticle?.publishedAt" icon="pi pi-send" label="Veröffentlichen" size="small" @click="publish()" />
    <Button v-if="editMode && blogArticle?.publishedAt" icon="pi pi-eye-slash" label="Entwurf" size="small" severity="secondary" @click="unpublish()" />
    <Button v-if="editMode" icon="pi pi-check" label="Speichern" size="small" @click="save()" />
    <Button v-if="editMode && blogArticle!.id !== 0" icon="pi pi-trash" label="Entfernen" size="small" severity="danger" @click="confirmRemoval()" />
  </div>
</template>

<style>
.articleContent {
  font-family: "Helvetica Neue", sans-serif;
  font-size: 18px;
  letter-spacing: 0.3px;
  font-weight: 300;
  line-height: 1.7;
  color: rgb(214 211 209);
}

.articleContent img {
  margin-bottom: 28px;
  border-radius: 8px;
  width: 100%;
  object-fit: contain;
  max-width: 100%;
  height: auto;
}

.articleContent p {
  line-height: 1.7;
  margin-bottom: 24px;
}

.articleContent a {
  color: #E39737;
  text-decoration: underline;
}

.articleContent h2 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin: 36px 0 14px;
}

.articleContent h3 {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin: 28px 0 10px;
}

.articleContent h4 {
  font-size: 17px;
  font-weight: 600;
  color: #e4e4e7;
  margin: 20px 0 8px;
}

.articleContent ul {
  list-style-type: none;
  padding-left: 0;
  margin: 20px 0;
}

.articleContent ul li {
  position: relative;
  padding-left: 28px;
  margin-bottom: 12px;
  font-size: 17px;
  color: rgb(214 211 209);
}

.articleContent ul li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background-color: #E39737;
  border-radius: 50%;
}

.articleContent ul li strong {
  color: #ffffff;
}

.articleContent blockquote {
  border-left: 3px solid #E39737;
  margin: 24px 0;
  padding: 10px 20px;
  background: rgba(227, 151, 55, 0.05);
  border-radius: 0 6px 6px 0;
  color: rgb(168 162 158);
  font-style: italic;
}
</style>
